import { useEffect, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { AuthShell } from '@/components/layout/AuthShell';
import { Alert } from '@/components/ui/Alert';
import { verifyEmail } from '@/api/auth';
import { describeError } from '@/lib/errorMessage';

const searchSchema = z.object({
  token: z.string().min(1).optional(),
});

export const Route = createFileRoute('/verify-email')({
  validateSearch: searchSchema,
  component: VerifyEmailPage,
});

function VerifyEmailPage() {
  const { t } = useTranslation(['auth', 'errors']);
  const { token } = Route.useSearch();
  const [state, setState] = useState<'loading' | 'success' | 'error'>(
    token ? 'loading' : 'error'
  );
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setState('error');
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        await verifyEmail(token);
        if (!cancelled) setState('success');
      } catch (e) {
        if (!cancelled) {
          setState('error');
          setMessage(describeError(t, e));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, t]);

  return (
    <AuthShell title={t(state === 'success' ? 'verify.successTitle' : 'verify.title')}>
      {state === 'loading' ? (
        <div className="flex items-center justify-center p-6">
          <span className="size-8 animate-spin rounded-full border-2 border-[color:var(--color-accent)] border-t-transparent" />
        </div>
      ) : state === 'success' ? (
        <Alert variant="success" title={t('verify.successTitle')}>
          {t('verify.successBody')}
        </Alert>
      ) : !token ? (
        <Alert variant="warn" title={t('verify.missingTokenTitle')}>
          {t('verify.missingTokenBody')}
        </Alert>
      ) : (
        <Alert variant="danger" title={t('errors:invalid_token')}>
          {message ?? t('errors:invalid_token')}
        </Alert>
      )}
    </AuthShell>
  );
}
